/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import React from 'react';
import { Box, Button, Chip, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { CLIENT_LABELS } from '../mcpConnect/types';
import type { McpCatalogEntry } from './entries';
import { MORPH_SURFACE, MORPH_TITLE } from './viewTransition';

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex', flexDirection: 'column', gap: theme.spacing(1),
    padding: theme.spacing(2), borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper,
    minWidth: 0,
  },
  head: { display: 'flex', alignItems: 'baseline', gap: theme.spacing(1), flexWrap: 'wrap' },
  title: { fontSize: '1rem', fontWeight: 600, margin: 0 },
  provider: { fontSize: '0.8125rem', color: theme.palette.text.secondary },
  description: { fontSize: '0.8125rem', flex: 1 },
  clients: { fontSize: '0.75rem', color: theme.palette.text.secondary },
  footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1), flexWrap: 'wrap' },
}));

const useChipStyles = makeStyles(theme => ({
  // MUI's small chip is a fixed 24px with the label flush to its border; let it
  // size to the label with a real vertical inset.
  root: { height: 'auto', padding: theme.spacing(0.25, 0) },
}));

/** The lifecycle badge, shared by the card and the dialog. */
export const ExperimentalChip = () => {
  const classes = useChipStyles();
  return <Chip size="small" variant="outlined" label="Experimental" classes={{ root: classes.root }} />;
};

/** Marks a stdio server, so it reads differently from a hosted one on the grid. */
export const LocalChip = () => {
  const classes = useChipStyles();
  return (
    <Tooltip title="Runs on your machine — your AI client starts it locally">
      <Chip size="small" variant="outlined" label="Local" classes={{ root: classes.root }} />
    </Tooltip>
  );
};

export const McpServerCard = ({ entry, onConnect, morphing = false }: { entry: McpCatalogEntry; onConnect: () => void; morphing?: boolean }) => {
  const classes = useStyles();
  const { descriptor } = entry;
  const headingId = React.useId();
  return (
    <article className={classes.card} aria-labelledby={headingId} style={morphing ? { viewTransitionName: MORPH_SURFACE } : undefined}>
      <Box className={classes.head}>
        <Typography component="h3" id={headingId} className={classes.title} style={morphing ? { viewTransitionName: MORPH_TITLE } : undefined}>
          {descriptor.title}
        </Typography>
        <Typography component="span" className={classes.provider}>by {entry.provider}</Typography>
        {descriptor.transport.kind === 'local' && <LocalChip />}
        {entry.experimental && <ExperimentalChip />}
      </Box>
      {entry.description && <Typography className={classes.description}>{entry.description}</Typography>}
      <Typography className={classes.clients}>
        Works with {descriptor.clients.map(c => CLIENT_LABELS[c]).join(' · ')}
      </Typography>
      <Box className={classes.footer}>
        <Typography component="span" className={classes.clients}>{entry.tags.join(' · ')}</Typography>
        <Button variant="contained" color="primary" size="small" onClick={onConnect} aria-label={`Connect ${descriptor.title}`}>
          Connect
        </Button>
      </Box>
    </article>
  );
};
